import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  MessageCircle, 
  X, 
  Wrench,
  Clock,
  User,
  Phone,
  Send,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MaintenanceAlert({ showCompact = false }) {
  const [alerts, setAlerts] = useState([]);
  const [usersWithPhone, setUsersWithPhone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = authHeaders();

      const [alertsRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/maintenance/alerts`, { headers }),
        fetch(`${API_URL}/api/users/with-phone`, { headers })
      ]);

      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsersWithPhone(data);
      }
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSendModal = (alert) => {
    setSelectedAlert(alert);
    setSelectedUser('');
    setShowModal(true);
  };

  const sendWhatsAppAlert = () => {
    if (!selectedUser || !selectedAlert) {
      toast.error('Selecione um destinatário');
      return;
    }

    const user = usersWithPhone.find(u => u.id === selectedUser);
    if (!user) {
      toast.error('Usuário não encontrado');
      return;
    }

    // Formatar telefone (remover caracteres não numéricos)
    const phone = user.phone.replace(/\D/g, '');
    // Adicionar código do país se não tiver
    const phoneFormatted = phone.startsWith('55') ? phone : `55${phone}`;

    // Status do alerta
    const statusEmoji = selectedAlert.status === 'urgent' ? '🚨' : '⚠️';
    const statusText = selectedAlert.status === 'urgent' ? 'URGENTE' : 'ATENÇÃO';

    // Informações da última operação
    const locationInfo = selectedAlert.last_location ? `📍 *Local atual:* ${selectedAlert.last_location}` : '';
    const operatorInfo = selectedAlert.last_operator ? `👷 *Operador:* ${selectedAlert.last_operator}` : '';
    const operationInfo = selectedAlert.last_operation_type ? `🔄 *Operação:* ${selectedAlert.last_operation_type}` : '';
    
    // Montar bloco de localização apenas se houver informações
    const locationBlock = [locationInfo, operatorInfo, operationInfo].filter(Boolean).join('\n');

    // Montar mensagem
    const message = `${statusEmoji} *ALERTA DE MANUTENÇÃO*
_Impulso Digital_

*Máquina:* ${selectedAlert.machine_name}
*Nº Série:* ${selectedAlert.serial_number}
${locationBlock ? `\n${locationBlock}\n` : ''}
📊 *Horímetro atual:* ${selectedAlert.current_hours}h
🎯 *Próxima revisão:* ${selectedAlert.maintenance_interval}h
⏰ *Restam:* ${selectedAlert.hours_until_maintenance}h

🔔 *Status:* ${statusText}

Por favor, providenciar manutenção preventiva.`;

    // Codificar mensagem para URL
    const encodedMessage = encodeURIComponent(message);

    // Abrir WhatsApp
    window.open(`https://wa.me/${phoneFormatted}?text=${encodedMessage}`, '_blank');

    toast.success(`Abrindo WhatsApp para ${user.name}...`);
    setShowModal(false);
  };

  if (loading) {
    return null;
  }

  if (alerts.length === 0) {
    return null;
  }

  // Versão compacta para exibir em outras telas
  if (showCompact) {
    return (
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-orange-500" size={20} />
            <span className="text-orange-500 font-semibold">
              {alerts.length} máquina{alerts.length > 1 ? 's' : ''} com manutenção pendente
            </span>
          </div>
          {expanded ? <ChevronUp size={20} className="text-orange-500" /> : <ChevronDown size={20} className="text-orange-500" />}
        </div>

        {expanded && (
          <div className="mt-4 space-y-2">
            {alerts.map((alert) => (
              <div 
                key={alert.machine_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  alert.status === 'urgent' 
                    ? 'bg-red-500/10 border border-red-500/30' 
                    : 'bg-yellow-500/10 border border-yellow-500/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Wrench size={16} className={alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'} />
                  <div>
                    <p className="text-white text-sm font-medium">{alert.machine_name}</p>
                    <p className={`text-xs ${alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'}`}>
                      Restam {alert.hours_until_maintenance}h
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openSendModal(alert);
                  }}
                  className="p-2 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                  title="Enviar alerta via WhatsApp"
                >
                  <MessageCircle size={16} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Modal de envio */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <MessageCircle className="text-green-500" size={20} />
                  Enviar Alerta WhatsApp
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Info da máquina */}
                {selectedAlert && (
                  <div className={`p-3 rounded-lg ${
                    selectedAlert.status === 'urgent' 
                      ? 'bg-red-500/10 border border-red-500/30' 
                      : 'bg-yellow-500/10 border border-yellow-500/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench size={16} className={selectedAlert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'} />
                      <span className="text-white font-medium">{selectedAlert.machine_name}</span>
                    </div>
                    
                    {/* Informações da última operação */}
                    {(selectedAlert.last_location || selectedAlert.last_operator || selectedAlert.last_operation_type) && (
                      <div className="mb-2 pb-2 border-b border-gray-700 space-y-1 text-sm">
                        {selectedAlert.last_location && (
                          <p className="text-gray-300">📍 {selectedAlert.last_location}</p>
                        )}
                        {selectedAlert.last_operator && (
                          <p className="text-gray-300">👷 {selectedAlert.last_operator}</p>
                        )}
                        {selectedAlert.last_operation_type && (
                          <p className="text-gray-300">🔄 {selectedAlert.last_operation_type}</p>
                        )}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-gray-400">
                        <Clock size={14} />
                        <span>Atual: {selectedAlert.current_hours}h</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400">
                        <span>Revisão: {selectedAlert.maintenance_interval}h</span>
                      </div>
                    </div>
                    <p className={`mt-2 text-sm font-semibold ${
                      selectedAlert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      ⏰ Restam {selectedAlert.hours_until_maintenance}h - {selectedAlert.status === 'urgent' ? 'URGENTE' : 'ATENÇÃO'}
                    </p>
                  </div>
                )}

                {/* Seletor de destinatário */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <User size={14} className="inline mr-1" />
                    Enviar para:
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none"
                  >
                    <option value="">Selecione o destinatário</option>
                    {usersWithPhone.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role_label}) - {user.phone}
                      </option>
                    ))}
                  </select>
                  {usersWithPhone.length === 0 && (
                    <p className="text-yellow-500 text-xs mt-1">
                      Nenhum usuário com telefone cadastrado
                    </p>
                  )}
                </div>

                {/* Botão enviar */}
                <button
                  onClick={sendWhatsAppAlert}
                  disabled={!selectedUser}
                  className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                    selectedUser 
                      ? 'bg-green-600 hover:bg-green-500 text-white' 
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Send size={18} />
                  Abrir WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Versão completa (para Dashboard)
  return (
    <div className="bg-[#111] border border-orange-500/30 rounded-xl">
      <div className="p-4 lg:p-6 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-orange-500" size={20} />
            <h2 className="text-lg font-semibold text-white">Alertas de Manutenção</h2>
            <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-1 rounded-full">
              {alerts.length}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-6 space-y-3">
        {alerts.map((alert) => (
          <div 
            key={alert.machine_id}
            className={`flex items-center justify-between p-4 rounded-lg ${
              alert.status === 'urgent' 
                ? 'bg-red-500/10 border border-red-500/30' 
                : 'bg-yellow-500/10 border border-yellow-500/30'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${
                alert.status === 'urgent' ? 'bg-red-500/20' : 'bg-yellow-500/20'
              }`}>
                <Wrench size={20} className={alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'} />
              </div>
              <div>
                <p className="text-white font-medium">{alert.machine_name}</p>
                <p className="text-gray-400 text-sm">Nº {alert.serial_number}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className={`font-semibold ${
                  alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {alert.hours_until_maintenance}h restantes
                </p>
                <p className="text-gray-400 text-xs">
                  {alert.status === 'urgent' ? '🚨 URGENTE' : '⚠️ ATENÇÃO'}
                </p>
              </div>
              <button
                onClick={() => openSendModal(alert)}
                className="p-3 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                title="Enviar alerta via WhatsApp"
              >
                <MessageCircle size={20} className="text-white" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de envio */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageCircle className="text-green-500" size={20} />
                Enviar Alerta WhatsApp
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Info da máquina */}
              {selectedAlert && (
                <div className={`p-3 rounded-lg ${
                  selectedAlert.status === 'urgent' 
                    ? 'bg-red-500/10 border border-red-500/30' 
                    : 'bg-yellow-500/10 border border-yellow-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench size={16} className={selectedAlert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'} />
                    <span className="text-white font-medium">{selectedAlert.machine_name}</span>
                  </div>
                  
                  {/* Informações da última operação */}
                  {(selectedAlert.last_location || selectedAlert.last_operator || selectedAlert.last_operation_type) && (
                    <div className="mb-2 pb-2 border-b border-gray-700 space-y-1 text-sm">
                      {selectedAlert.last_location && (
                        <p className="text-gray-300">📍 {selectedAlert.last_location}</p>
                      )}
                      {selectedAlert.last_operator && (
                        <p className="text-gray-300">👷 {selectedAlert.last_operator}</p>
                      )}
                      {selectedAlert.last_operation_type && (
                        <p className="text-gray-300">🔄 {selectedAlert.last_operation_type}</p>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1 text-gray-400">
                      <Clock size={14} />
                      <span>Atual: {selectedAlert.current_hours}h</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <span>Revisão: {selectedAlert.maintenance_interval}h</span>
                    </div>
                  </div>
                  <p className={`mt-2 text-sm font-semibold ${
                    selectedAlert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    ⏰ Restam {selectedAlert.hours_until_maintenance}h - {selectedAlert.status === 'urgent' ? 'URGENTE' : 'ATENÇÃO'}
                  </p>
                </div>
              )}

              {/* Seletor de destinatário */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <User size={14} className="inline mr-1" />
                  Enviar para:
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none"
                >
                  <option value="">Selecione o destinatário</option>
                  {usersWithPhone.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role_label}) - {user.phone}
                    </option>
                  ))}
                </select>
                {usersWithPhone.length === 0 && (
                  <p className="text-yellow-500 text-xs mt-1">
                    Nenhum usuário com telefone cadastrado
                  </p>
                )}
              </div>

              {/* Botão enviar */}
              <button
                onClick={sendWhatsAppAlert}
                disabled={!selectedUser}
                className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                  selectedUser 
                    ? 'bg-green-600 hover:bg-green-500 text-white' 
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send size={18} />
                Abrir WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
